"use client";

import ClearIcon from "@mui/icons-material/ClearRounded";
import SearchIcon from "@mui/icons-material/SearchRounded";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";

import type { SxProps, Theme } from "@mui/material/styles";

export type SearchFieldProps = {
  value: string;
  /**
   * Reçoit la valeur, pas l'évènement : les six copies remplacées faisaient
   * toutes `e.target.value` et rien d'autre.
   */
  onChange: (value: string) => void;
  placeholder: string;
  /**
   * Vide le champ.
   *
   * Fourni, un bouton d'effacement apparaît dès que le champ n'est plus vide.
   * Sans lui, l'utilisateur doit sélectionner sa saisie pour l'effacer, ce qui
   * est le geste le plus coûteux d'un champ dont on ressort souvent.
   */
  onClear?: () => void;
  /** Aria-label du bouton d'effacement — il n'a qu'une icône. */
  clearLabel?: string;
  fullWidth?: boolean;
  size?: "small" | "medium";
  /** Réservé au placement dans la rangée (`flex`, `maxWidth`, marges). */
  sx?: SxProps<Theme>;
  /**
   * Posé sur l'`<input>` et non sur la racine, pour qu'un test puisse taper
   * dedans et lire sa valeur directement.
   */
  "data-testid"?: string;
  /**
   * Nom accessible du champ quand aucun libellé visible ne l'accompagne : le
   * placeholder ne compte pas comme nom.
   */
  "aria-label"?: string;
};

/**
 * Champ de recherche : un `TextField` outlined avec la loupe en tête.
 *
 * Il remplace six variantes — quatre qui portaient la loupe, deux qui
 * l'avaient oubliée — et l'unique champ de recherche d'odys. C'est justement
 * cet oubli qui motive le composant : rien ne signalait qu'un champ était une
 * recherche plutôt qu'un champ de saisie ordinaire.
 *
 * Le fond blanc sur le fond de page beige n'est pas son affaire : le socle le
 * donne déjà à tout `MuiOutlinedInput` (issue #2505).
 */
export function SearchField({
  value,
  onChange,
  placeholder,
  onClear,
  clearLabel = "Clear search",
  fullWidth,
  size = "small",
  sx,
  "data-testid": dataTestId,
  "aria-label": ariaLabel,
}: SearchFieldProps) {
  return (
    <TextField
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      size={size}
      fullWidth={fullWidth}
      sx={sx}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment:
            onClear === undefined || value === "" ? undefined : (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  edge="end"
                  onClick={onClear}
                  aria-label={clearLabel}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
        },
        htmlInput: {
          ...(dataTestId === undefined ? {} : { "data-testid": dataTestId }),
          ...(ariaLabel === undefined ? {} : { "aria-label": ariaLabel }),
        },
      }}
    />
  );
}
